import { useEffect, useState } from "react";
import {
  ApiError,
  readIct,
  readReadiness,
  recordIctGate,
  resendAccessLink,
  type IctStanding,
  fetchAuditDocument,
  fetchAuditDocumentArchive,
  type Readiness as ReadinessRecord,
} from "../api/client";
import { IctGatesCard } from "./IctGates";

/**
 * Screens 43 and 44 — pre-audit readiness, and the MD 4 gates that sit beside them.
 *
 * ⚠ **There is no preliminary review on this screen any more, and no completeness on it
 * either.** ADR 0025. Nothing reads the client's documents between the plan being issued and
 * the session opening, so `DocumentReview.tsx` is gone; and evidence is not bound to a clause,
 * so there is no set of slots to complete and no "Ready for audit day" to earn. What the lead
 * auditor gets is the evidence itself — every document the client sent, each one openable —
 * and the expected list beside it as a reference for what an audit of these standards usually
 * looks at.
 *
 * **The auditor's access is a permission, not a link.** They reach the audit through their
 * own signed-in account, which is what makes every action attributable to them personally —
 * and it is what makes their eventual sign-off defensible.
 *
 * **The auditee's access is a link, and this screen is where it is read.** It is masked
 * until the auditor asks for it, and it comes from the server rather than from the step that
 * minted it — so it is here on the reader's first arrival and on every arrival afterwards,
 * across a sign-out and a different machine, for exactly as long as the link works. When the
 * session closes the sealed copy is destroyed and this row says so, which is the same moment
 * the link stops working: one fact, not two.
 */
export function ReadinessScreen({ auditId }: { auditId: string }) {
  const [readiness, setReadiness] = useState<ReadinessRecord | null>(null);
  const [ict, setIct] = useState<IctStanding | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  /**
   * Masked until asked for. The link is available on every visit now, which is precisely why
   * it stays covered by default: a credential drawn on arrival is a credential in front of
   * whoever is standing behind the auditor, at the one moment they were not expecting it.
   * Revealing it is a deliberate act, and it resets on every arrival.
   */
  const [shown, setShown] = useState(false);
  /** Momentary, and it says what happened rather than what was pressed. */
  const [copied, setCopied] = useState<"done" | "failed" | null>(null);
  /** The whole shelf, in one download. In flight while the zip is being built server-side. */
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    readReadiness(auditId)
      .then(setReadiness)
      .catch((cause: unknown) => setError(problem(cause, "Readiness")));
    readIct(auditId)
      .then(setIct)
      .catch(() => undefined);
  }, [auditId]);

  /**
   * Every document the client sent, as one zip — M15-6's register, taken away whole.
   *
   * The archive is built on the server, so the failure worth reporting is the request's:
   * a lead auditor who presses this and gets nothing must be told, because the alternative
   * is preparing for an audit day believing the evidence is on their machine.
   */
  const downloadAll = async () => {
    setDownloading(true);
    setError(null);
    try {
      const archive = await fetchAuditDocumentArchive(auditId);
      const anchor = document.createElement("a");
      anchor.href = archive.url;
      anchor.download = archive.filename;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(archive.url), 0);
    } catch (cause) {
      setError(problem(cause, "The documents"));
    } finally {
      setDownloading(false);
    }
  };

  if (error && !readiness) {
    return (
      <p className="problem" role="alert">
        {error}
      </p>
    );
  }
  if (!readiness) return <p className="sub">Reading readiness&hellip;</p>;

  const record = async (gate: string, satisfied: boolean) => {
    try {
      setIct(await recordIctGate(auditId, gate, satisfied));
    } catch (cause) {
      setError(problem(cause, "The gate"));
    }
  };

  /**
   * The link as the auditee receives it, or null when there is none to show.
   *
   * Null covers four situations the reader does not need told apart: the link was issued
   * before the platform kept a readable copy, a re-issue revoked it, the session closed and
   * took the copy with it, or no link has been issued at all. In every one of them the true
   * sentence is the same — there is no live link here to read.
   *
   * A fragment, never a query string, so the token is never sent to a server and never
   * lands in an access log. That is how the portal reads it too.
   */
  const link = readiness.access.token
    ? `${location.origin}/portal#${readiness.access.token}`
    : null;

  const copy = async () => {
    if (link === null) return;
    setCopied(null);
    try {
      await navigator.clipboard.writeText(link);
      setCopied("done");
    } catch {
      // A clipboard write needs a secure context and the browser's permission, and either
      // can refuse. Saying so is the point: a Copy button that fails quietly leaves the
      // auditor pasting whatever was on the clipboard before, into an email to the client.
      setCopied("failed");
    }
  };

  /**
   * Send the auditee their link again — M9-0.
   *
   * **Destructive, and the confirmation says so.** This mints a replacement and revokes the
   * first, so an auditee who *is* holding the original loses it here — which is why it asks
   * before firing.
   *
   * It stays available now that the link can simply be read: re-issuing answers a different
   * question. *Show link* is for an auditor who needs the link in front of them; this is for
   * a link that went to the wrong address, or one that has to be invalidated.
   */
  const resend = async () => {
    const address = readiness.access.issued_to ?? "the auditee";
    if (
      !window.confirm(
        `Send a new access link to ${address}?\n\nThe link already issued stops working ` +
          "immediately. If the auditee is holding it, they lose access until they open the " +
          "new one.",
      )
    ) {
      return;
    }
    setError(null);
    setResending(true);
    try {
      await resendAccessLink(auditId, crypto.randomUUID());
      setCopied(null);
      // Revealed without being asked, unlike an ordinary arrival: this reader has just
      // confirmed a destructive dialog in order to obtain exactly this string. The token
      // itself comes from the re-read, not from the response — one source, so a failed
      // re-read shows nothing rather than showing a link the screen cannot corroborate.
      setShown(true);
      setReadiness(await readReadiness(auditId));
    } catch (cause) {
      setError(problem(cause, "The access link"));
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <div className="between" style={{ marginBottom: "14px" }}>
        <div>
          <div className="eyebrow">Steps 4 and 5 · Lead auditor</div>
          <h1>Pre-audit readiness</h1>
          <p className="sub">
            <span className="mono">{readiness.reference}</span> ·{" "}
            {readiness.client_organisation} · audit day {String(readiness.planned_date)}
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <span className={readiness.documents_received ? "pill p-ok" : "pill p-wait"}>
            {readiness.documents_received
              ? `${readiness.documents_received} document${readiness.documents_received === 1 ? "" : "s"} received`
              : "Awaiting documents"}
          </span>
        </div>
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <div className="grid2">
        <div className="card">
          <h2>Access</h2>
          <dl className="kv" style={{ marginTop: "14px" }}>
            <dt>Auditee link</dt>
            <dd>
              {readiness.access.issued ? (
                <>
                  <span className="pill p-ok">Issued</span> to{" "}
                  {readiness.access.issued_to ?? "—"}
                </>
              ) : (
                <span className="pill p-mute">Not yet issued</span>
              )}
            </dd>
            {/*
              The link itself, in the register beside the address it went to and the date it
              was granted \u2014 the same three facts a reader comes to this card for, and the
              reason it is a row here rather than a callout below. It is masked, so the row
              is a statement that a link exists and a control that puts it on screen; it is
              not the credential, drawn at whoever is reading over the auditor's shoulder.
            */}
            {readiness.access.issued ? (
              <>
                <dt>Link</dt>
                <dd>
                  {link === null ? (
                    <span className="sub">
                      Not readable. The link was issued before Complao kept a readable copy,
                      or the session has closed and the copy went with it. Re-issuing below
                      produces one that can be read &mdash; and stops the current link
                      working.
                    </span>
                  ) : (
                    <>
                      <div className={shown ? "link-box" : "link-box masked"}>
                        {shown ? link : "\u2022".repeat(44)}
                      </div>
                      <div className="row" style={{ gap: "8px", marginTop: "8px" }}>
                        <button
                          type="button"
                          className="btn sec sm"
                          aria-expanded={shown}
                          onClick={() => setShown((visible) => !visible)}
                        >
                          {shown ? "Hide link" : "Show link"}
                        </button>
                        <button type="button" className="btn sec sm" onClick={() => void copy()}>
                          Copy
                        </button>
                        {/*
                          Polite, not assertive: the auditor is looking at the button they
                          just pressed, and an assertive live region interrupts a screen
                          reader mid-sentence to say something they already expect.
                        */}
                        <span className="sub" role="status" aria-live="polite">
                          {copied === "done"
                            ? "Copied to the clipboard."
                            : copied === "failed"
                              ? "The browser refused the clipboard \u2014 show the link and copy it by hand."
                              : ""}
                        </span>
                      </div>
                      <div className="sub" style={{ fontSize: "11px", marginTop: "5px" }}>
                        Single audit. Delivery is best effort, so if the mail did not arrive,
                        this is the copy that recovers it.
                      </div>
                    </>
                  )}
                </dd>
              </>
            ) : null}
            <dt>Granted</dt>
            <dd>{stamp(readiness.access.issued_at)}</dd>
            <dt>Auditee link expires</dt>
            <dd>
              On session close. The auditee cannot sign in again afterwards, and the link
              survives any pause in between. The readable copy above ends at the same moment,
              because it is the same link.
            </dd>
          </dl>

          {readiness.access.issued ? (
            <div style={{ marginTop: "14px" }}>
              <button
                type="button"
                className="btn sec sm"
                onClick={() => void resend()}
                disabled={resending}
              >
                {resending ? "Re-issuing\u2026" : "Re-issue and email the link"}
              </button>
              <p className="sub" style={{ marginTop: "8px" }}>
                The link was emailed when the plan was issued. Re-issuing sends it again to{" "}
                {readiness.access.issued_to ?? "the auditee"} and{" "}
                <b>stops the current link working</b>. To simply read the link, use{" "}
                <i>Show link</i> above &mdash; it costs the auditee nothing.
              </p>
            </div>
          ) : null}
        </div>

        <div className="card">
          <h2>Documents received</h2>
          <div
            className="row"
            style={{ alignItems: "baseline", gap: "8px", marginBottom: "10px" }}
          >
            <span
              style={{ fontSize: "26px", fontWeight: 600, color: "var(--colour-brand-deep-forest)" }}
            >
              {readiness.documents_received}
            </span>
            <span className="sub">
              document{readiness.documents_received === 1 ? "" : "s"} sent by the client
            </span>
            <span style={{ flex: 1 }} />
            {/* The whole shelf at once. An auditor preparing for the audit day reads the
                evidence away from this screen — on a second monitor, on a train — and the
                filename links open one file per press. Disabled rather than hidden while
                nothing has arrived: a control that vanishes reads as a feature the audit
                does not have. */}
            <button
              type="button"
              className="btn sec sm"
              disabled={downloading || readiness.documents_received === 0}
              onClick={() => void downloadAll()}
            >
              {downloading ? "Preparing\u2026" : "Download All"}
            </button>
          </div>

          {(readiness.documents ?? []).map((document) => (
            <div className="docrow" key={document.id}>
              <div className="g">
                {/* The filename opens the file. An audit is conducted against evidence, and
                    evidence that cannot be opened is evidence taken on trust. */}
                <button
                  type="button"
                  className="linky"
                  onClick={() => void open(auditId, document.id, document.filename)}
                >
                  {document.filename}
                </button>
                <small>
                  {document.uploaded_by} · {stamp(document.uploaded_at)}
                  {/* ⚠ A knowledge base does not OCR. A scan retrieves as nothing, and the
                      clause it was evidence for reads unevidenced with nothing saying why. */}
                  {document.extraction_ok === false ? " · no readable text" : ""}
                </small>
              </div>
              <span
                className={document.status === "reviewed" ? "pill p-ok" : "pill p-wait"}
              >
                {document.status === "reviewed" ? "Reviewed" : "Not reviewed"}
              </span>
            </div>
          ))}

          {(readiness.expected ?? []).length ? (
            <>
              <div className="hr" />
              <div className="eyebrow">Usually looked at for these standards</div>
              <p className="sub" style={{ fontSize: "11px", margin: "4px 0 8px" }}>
                A reference, not a checklist — nothing here is outstanding. ADR 0025.
              </p>
              {(readiness.expected ?? []).map((row) => (
                <div className="docrow" key={row.item_id}>
                  <span className="mono" style={{ width: "62px", flex: "none" }}>
                    Cl. {row.clause}
                  </span>
                  <div className="g">
                    {row.name}
                    {row.standard_specific ? (
                      <span className="sub"> ({row.standards.join(", ")})</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </>
          ) : null}

        </div>
      </div>

      {ict ? (
        <IctGatesCard
          standing={ict}
          step="session_start"
          onRecord={(gate, ok) => void record(gate, ok)}
        />
      ) : null}

    </>
  );
}

/**
 * Open one document in a new tab.
 *
 * Fetched rather than linked: the route is authenticated and an `<a href>` carries no bearer
 * token. The blob URL is revoked on the next tick — the tab has already taken the bytes, and
 * leaving it live keeps the whole file in memory for as long as the screen is open.
 */
async function open(auditId: string, documentId: string, filename: string): Promise<void> {
  const url = await fetchAuditDocument(auditId, documentId);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener";
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function stamp(value: string | null | undefined): string {
  return value ? value.slice(0, 16).replace("T", " ") : "—";
}

function problem(cause: unknown, subject: string): string {
  return cause instanceof ApiError ? cause.message : `${subject} could not be read.`;
}
